"use client";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { likeContentAction } from "@/actions/content.action";
import LikeIcon from "@/components/icons/like";
import { Button } from "@/components/ui/button";
import type { ApiResponse } from "@/lib/fetch";

interface Props {
  contentId: string;
  initialCount: number;
  initialLiked: boolean;
}

const LikeButtonClient = ({ contentId, initialCount, initialLiked }: Props) => {
  const [liked, setLiked] = useState(initialLiked);
  const [count, setCount] = useState(initialCount);

  type Snapshot = { count: number; liked: boolean };

  const { mutate, isPending } = useMutation<
    ApiResponse<unknown>,
    Error,
    boolean,
    Snapshot
  >({
    mutationFn: (currentLiked: boolean) =>
      likeContentAction(contentId, currentLiked),
    onError: (_err, _vars, snapshot) => {
      if (snapshot) {
        setLiked(snapshot.liked);
        setCount(snapshot.count);
      }
    },
    onMutate: (currentLiked) => {
      const snapshot = { count, liked };
      const newLiked = !currentLiked;
      setLiked(newLiked);
      setCount((c) => (newLiked ? c + 1 : c - 1));
      return snapshot;
    },
    onSuccess: (res, _vars, snapshot) => {
      if (!res.isSuccess && snapshot) {
        setLiked(snapshot.liked);
        setCount(snapshot.count);
      }
    },
  });

  return (
    <span className="inline-flex items-center justify-center">
      <Button
        className="group"
        disabled={isPending}
        onClick={() => mutate(liked)}
        size="icon-sm"
        variant="ghost"
      >
        <LikeIcon
          className={
            liked
              ? "size-6 stroke-[1.5] stroke-brand-blue-500 fill-brand-blue-100"
              : "size-6 stroke-[1.5] stroke-neutral-400 group-hover:stroke-brand-blue-500 group-hover:fill-brand-blue-100"
          }
        />
      </Button>
      {count}
    </span>
  );
};

export { LikeButtonClient };
